// 管理员工单管理:列表 + 详情/回复
// 风格与其他管理页面一致:用 Semi Table
import React, { useEffect, useState } from 'react';
import { Button, Modal, Input, TextArea, Toast, Spin, Tag, Empty, Tabs, Table } from '@douyinfe/semi-ui';
import { API } from '../helpers';
import { markTicketReadByUpdatedAt } from '../components/AdminTicketsNavButton';

const STATUS_LABEL = { 1: { text: '进行中', color: 'orange' }, 2: { text: '已关闭', color: 'grey' } };

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // 详情打开的工单 id
  const [tab, setTab] = useState('open');       // open / closed
  const [reloadKey, setReloadKey] = useState(0);
  const [actingId, setActingId] = useState(null); // 当前正在操作某行的 ticketId(loading 状态)

  const load = () => {
    setLoading(true);
    API.get(`/api/tickets?status=${tab}`)
      .then((r) => { if (r.data?.success) setTickets(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, reloadKey]);

  // 进入工单管理页面时,把所有未读工单按各自 updated_at 标记为已读
  useEffect(() => {
    API.get('/api/tickets')
      .then((r) => {
        if (r.data?.success) {
          r.data.data.forEach((tk) => {
            if (tk.status === 1 && tk.last_reply_by === 1) {
              markTicketReadByUpdatedAt(tk.updated_at);
            }
          });
        }
      })
      .catch(() => {});
  }, []);

  // —— 列表行上的关闭 / 重开操作 ——
  const confirmClose = async (id) => {
    const ok = await new Promise((resolve) => {
      Modal.confirm({
        title: '关闭工单?',
        content: '关闭后用户将无法再回复,如需继续可重新开启',
        okText: '确认关闭',
        cancelText: '取消',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!ok) return;
    setActingId(id);
    try {
      const r = await API.post(`/api/tickets/${id}/close`);
      if (r.data?.success) { Toast.success('已关闭'); setReloadKey((k) => k + 1); }
      else Toast.error(r.data?.message || '操作失败');
    } catch (e) { Toast.error(e.response?.data?.message || '操作失败'); }
    finally { setActingId(null); }
  };

  const reopenTicket = async (id) => {
    setActingId(id);
    try {
      const r = await API.post(`/api/tickets/${id}/reopen`);
      if (r.data?.success) { Toast.success('已重新开启'); setReloadKey((k) => k + 1); }
      else Toast.error(r.data?.message || '操作失败');
    } catch (e) { Toast.error(e.response?.data?.message || '操作失败'); }
    finally { setActingId(null); }
  };

  const confirmDelete = async (id) => {
    const ok = await new Promise((resolve) => {
      Modal.confirm({
        title: '删除工单?',
        content: '将永久删除该工单及其所有对话记录,且不可恢复。',
        okText: '确认删除',
        cancelText: '取消',
        okButtonProps: { type: 'danger' },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!ok) return;
    setActingId(id);
    try {
      const r = await API.post(`/api/tickets/${id}/delete`);
      if (r.data?.success) { Toast.success('已删除'); setReloadKey((k) => k + 1); }
      else Toast.error(r.data?.message || '删除失败');
    } catch (e) { Toast.error(e.response?.data?.message || '删除失败'); }
    finally { setActingId(null); }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '主题', dataIndex: 'subject', width: 280, ellipsis: true,
      render: (s, row) => (
        <span>
          {s || '(无主题)'}
          {row.last_reply_by === 1 && row.status === 1 && (
            <span style={{ marginLeft: 8, color: '#f56565', fontSize: 12, fontWeight: 500 }}>● 待回复</span>
          )}
        </span>
      ),
    },
    { title: '令牌', dataIndex: 'token_name', width: 200, ellipsis: true },
    { title: '消息数', dataIndex: 'msg_count', width: 90 },
    { title: '更新于', dataIndex: 'updated_at', width: 180, render: (ts) => ts ? new Date(ts).toLocaleString('zh-CN', { hour12: false }) : '—' },
    { title: '状态', dataIndex: 'status', width: 100, render: (s) => <Tag color={STATUS_LABEL[s]?.color || 'grey'} size="small">{STATUS_LABEL[s]?.text || '—'}</Tag> },
    {
      title: '操作', width: 210, fixed: 'right',
      render: (_, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          <Button size="small" theme="borderless" type="primary" style={{ padding: '0 6px' }} onClick={() => setActive(row.id)}>查看</Button>
          <span style={{ color: '#c9cdd4', userSelect: 'none' }}>|</span>
          {row.status === 1
            ? <Button size="small" theme="borderless" type="danger" style={{ padding: '0 6px' }} loading={actingId === row.id} onClick={() => confirmClose(row.id)}>关闭</Button>
            : <Button size="small" theme="borderless" type="primary" style={{ padding: '0 6px' }} loading={actingId === row.id} onClick={() => reopenTicket(row.id)}>重开</Button>
          }
          <span style={{ color: '#c9cdd4', userSelect: 'none' }}>|</span>
          <Button size="small" theme="borderless" type="danger" style={{ padding: '0 6px' }} loading={actingId === row.id} onClick={() => confirmDelete(row.id)}>删除</Button>
        </span>
      ),
    },
  ];

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 16px' }}>工单管理</h2>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        type="line"
        style={{ marginBottom: 12 }}
      >
        <Tabs.TabPane tab="进行中" itemKey="open" />
        <Tabs.TabPane tab="已关闭" itemKey="closed" />
        <Tabs.TabPane tab="全部" itemKey="all" />
      </Tabs>

      <div style={{ background: '#fff', borderRadius: 10, padding: 8, border: '1px solid #e5e6eb' }}>
        <Spin spinning={loading}>
          <Table
            size="small"
            rowKey="id"
            dataSource={tickets}
            columns={columns}
            scroll={{ x: 1280 }}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            empty={<Empty description="暂无工单" />}
          />
        </Spin>
      </div>

      <AdminTicketDetailModal
        ticketId={active}
        onClose={() => { setActive(null); setReloadKey((k) => k + 1); }}
      />
    </div>
  );
};

const AdminTicketDetailModal = ({ ticketId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!ticketId) { setData(null); return; }
    setLoading(true);
    API.get(`/api/tickets/${ticketId}`)
      .then((r) => {
        if (r.data?.success) {
          setData(r.data.data);
          if (r.data.data?.ticket?.updated_at) {
            markTicketReadByUpdatedAt(r.data.data.ticket.updated_at);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ticketId, reloadKey]);

  const sendReply = async () => {
    if (!reply.trim()) return Toast.error('请输入回复');
    setReplying(true);
    try {
      const r = await API.post(`/api/tickets/${ticketId}/reply`, { content: reply.trim() });
      if (r.data?.success) { setReply(''); setReloadKey((k) => k + 1); Toast.success('已回复'); }
      else Toast.error(r.data?.message || '回复失败');
    } catch (e) { Toast.error(e.response?.data?.message || '回复失败'); }
    finally { setReplying(false); }
  };

  const ticket = data?.ticket;
  const messages = data?.messages || [];
  const closed = ticket?.status === 2;

  return (
    <Modal
      title={ticket ? `#${ticket.id} · ${ticket.subject}` : '工单详情'}
      visible={!!ticketId}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
          <Button onClick={onClose}>关闭</Button>
          {!closed && (
            <Button type="primary" theme="solid" loading={replying} disabled={!reply.trim()} onClick={sendReply}>
              发送回复
            </Button>
          )}
        </div>
      }
      width={700}
      style={{ top: 40 }}
    >
      {loading || !data ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin spinning /></div>
      ) : (
        <div>
          <div style={{ fontSize: 12, color: '#86909c', marginBottom: 12 }}>
            <Tag color={STATUS_LABEL[ticket.status]?.color} style={{ marginRight: 8 }}>{STATUS_LABEL[ticket.status]?.text}</Tag>
            令牌 {ticket.token_name || '—'}
            · 创建于 {new Date(ticket.created_at).toLocaleString('zh-CN', { hour12: false })}
          </div>

          <div style={{ maxHeight: 360, overflowY: 'auto', background: '#fafbfc', padding: 12, borderRadius: 6, marginBottom: 12 }}>
            {messages.map((m) => (
              <div
                key={m.id}
                style={{
                  marginBottom: 10,
                  padding: '8px 12px',
                  borderRadius: 6,
                  background: m.author_type === 2 ? '#e6f4ff' : '#fff',
                  border: m.author_type === 2 ? '1px solid #91caff' : '1px solid #e5e6eb',
                }}
              >
                <div style={{ fontSize: 12, color: '#86909c', marginBottom: 4 }}>
                  {m.author_type === 2 ? '🛠 管理员(我)' : `👤 用户`}
                  · {new Date(m.created_at).toLocaleString('zh-CN', { hour12: false })}
                </div>
                <div style={{ fontSize: 14, color: '#1f2329', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {m.content}
                </div>
              </div>
            ))}
          </div>

          {!closed && (
            <TextArea
              value={reply}
              onChange={setReply}
              placeholder="输入回复..."
              rows={3}
              maxCount={2000}
              disabled={replying}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default AdminTickets;